import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogSection = () => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/blog`)
      .then((res) => setPosts(res.data.slice(0, 3)))
      .catch(() => setPosts([]));
  }, []);

  if (posts.length === 0) return null;

  return (
    <section className="py-32 bg-[#FAFAFA]" data-testid="latest-posts-section">
      <div className="container mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6"
        >
          <div>
            <span className="font-mono text-sm text-[#C8102E] tracking-tight">
              ~/latest-posts
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1A1A1A] mt-3 tracking-tight">
              From the field
            </h2>
          </div>
          <Link to="/blog" className="hidden md:block">
            <Button
              variant="ghost"
              className="text-[#1A1A1A] hover:bg-white rounded-full px-6 font-mono text-sm"
              data-testid="view-all-posts-button"
            >
              View all posts <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </motion.div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {posts.map((post, index) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              data-testid={`home-blog-card-${post.slug}`}
            >
              <Link to={`/blog/${post.slug}`} className="group block">
                <div className="relative overflow-hidden rounded-2xl aspect-[16/10] mb-6 bg-white">
                  <motion.img
                    src={post.cover_image}
                    alt={`Cover image for '${post.title}' — Hei Web Creations blog`}
                    loading="lazy"
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.6 }}
                  />
                </div>
                <div className="flex items-center gap-3 mb-3 font-mono text-xs">
                  <span className="text-[#C8102E] uppercase tracking-wider">
                    {post.category}
                  </span>
                  <span className="text-gray-300">/</span>
                  <span className="text-gray-400">{post.read_time}</span>
                </div>
                <h3 className="text-xl font-bold text-[#1A1A1A] leading-snug group-hover:text-[#C8102E] transition-colors duration-300">
                  {post.title}
                </h3>
                <p className="text-gray-500 mt-3 leading-relaxed line-clamp-2">
                  {post.excerpt}
                </p>
                <span className="inline-flex items-center gap-1 mt-4 font-mono text-sm text-[#1A1A1A] group-hover:gap-2 transition-all duration-300">
                  Read article <ArrowUpRight size={16} />
                </span>
              </Link>
            </motion.article>
          ))}
        </div>

        {/* Mobile view all */}
        <div className="mt-12 text-center md:hidden">
          <Link to="/blog">
            <Button className="bg-[#1A1A1A] hover:bg-[#C8102E] text-white rounded-full px-8 py-5">
              View all posts <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
